import { useMemo } from "react";
import { exportAdvancePayments } from "store/advancePaymentSlice";
import { exportAllArticles } from "store/articleStore";
import { exportBeneficiaryList } from "store/beneficiaryStore";
import { exportChannelList } from "store/channelStore";
import {
    fetchExportBeneficiariesFile,
    fetchExportFile,
    fetchExportPackageSales,
    fetchExportSales,
    fetchExportStocks,
} from "store/managementStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { exportTermList } from "store/termStore";
import { exportTitleFormatTypeList } from "store/titleFormatTypeStore";
import { exportEan13MatchingChannel, exportTitleList } from "store/titleStore";
import { exportWorkList } from "store/workStore";

import type { ExportItemConfig } from "./types";
import { ALL_PERIODS } from "./types";

// allPeriods dates may be Date objects (mutated to local time by periodStore) or API strings.
// Extract the local calendar day in either case.
const toLocalDateKey = (d: Date | string): string => {
    if (typeof d === "string") return d.slice(0, 10);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Custom hook that provides export configurations for different portal types.
 *
 * This hook encapsulates all the export action logic, keeping the business logic
 * separate from the UI components. Each export configuration defines:
 * - label: Unique identifier for i18n
 * - action: Function that performs the export (receives state object with period/dates)
 * - requiresPeriod/requiresDateRange: UI controls needed for this export
 *
 * @param usesStock - Whether the company uses stock management
 * @returns Object with export configurations for each portal type
 */
export const useExportConfigs = (usesStock: boolean) => {
    const dispatch = useAppDispatch();
    const { allPeriods } = useAppSelector((state) => state.period);

    return useMemo(() => {
        const bookExports: ExportItemConfig[] = [
            {
                label: "Works",
                action: () => dispatch(exportWorkList()),
            },
            {
                label: "Titles",
                action: () => dispatch(exportTitleList()),
            },
            {
                label: "TitlesEAN13MatchingChannel",
                action: () => dispatch(exportEan13MatchingChannel()),
            },
            {
                label: "Beneficiaries",
                action: () => dispatch(exportBeneficiaryList()),
            },
            {
                label: "Channels",
                action: () => dispatch(exportChannelList()),
            },
            {
                label: "FormatTypes",
                action: () => dispatch(exportTitleFormatTypeList()),
            },
            {
                label: "Terms",
                action: () => dispatch(exportTermList()),
            },
            {
                label: "AdvancePayments",
                requiresDateRange: true,
                action: ({ startDate, endDate }) => {
                    if (!startDate || !endDate) return;
                    return dispatch(
                        exportAdvancePayments({
                            startDate: toLocalDateKey(startDate),
                            endDate: toLocalDateKey(endDate),
                        }),
                    );
                },
            },
            {
                label: "Sales",
                requiresPeriod: true,
                defaultPeriod: ALL_PERIODS,
                allowAllPeriods: true,
                includeFuturePeriods: true,
                action: ({ period }) => {
                    if (period !== ALL_PERIODS) {
                        const periodData = allPeriods.find((p) => p.id === period);
                        if (periodData) {
                            dispatch(
                                fetchExportSales({
                                    startDate: `${toLocalDateKey(periodData.periodStart)}T00:00:00.000Z`,
                                    endDate: `${toLocalDateKey(periodData.periodEnd)}T23:59:59.999Z`,
                                }),
                            );
                        }
                    } else {
                        dispatch(fetchExportSales({ startDate: undefined, endDate: undefined }));
                    }
                },
            },
            {
                label: "Package",
                action: () => dispatch(fetchExportPackageSales()),
            },
            {
                label: "Management",
                requiresPeriod: true,
                action: ({ period }) => {
                    return dispatch(fetchExportFile(period!));
                },
            },
            {
                label: "ManagementBeneficiaries",
                requiresPeriod: true,
                action: ({ period }) => {
                    return dispatch(fetchExportBeneficiariesFile(period!));
                },
            },
        ];

        // Conditionally add Stock export if company uses stock
        if (usesStock) {
            bookExports.push({
                label: "Stock",
                requiresPeriod: true,
                action: ({ period }) => {
                    return dispatch(fetchExportStocks(period!));
                },
            });
        }

        const pressExports: ExportItemConfig[] = [
            {
                label: "Publications",
                action: () => dispatch(exportTitleList()),
            },
            {
                label: "Beneficiaries",
                action: () => dispatch(exportBeneficiaryList()),
            },
            {
                label: "Articles",
                requiresDateRange: true,
                action: ({ startDate, endDate }) => {
                    return dispatch(exportAllArticles({ startDate: startDate!, endDate: endDate! }));
                },
            },
        ];

        const audiovisualExports: ExportItemConfig[] = [
            {
                label: "Productions",
                action: () => dispatch(exportTitleList()),
            },
            {
                label: "Beneficiaries",
                action: () => dispatch(exportBeneficiaryList()),
            },
            {
                label: "Services",
                requiresDateRange: true,
                action: ({ startDate, endDate }) => {
                    return dispatch(exportAllArticles({ startDate: startDate!, endDate: endDate! }));
                },
            },
        ];

        return {
            book: bookExports,
            press: pressExports,
            audiovisual: audiovisualExports,
        };
    }, [dispatch, allPeriods, usesStock]);
};
